import { GetStaticProps } from "next";
import { useRef } from "react";

import { AppShell } from "@chef/feature-app-shell";
import {
  BlockContent,
  ITellAFriendCompetition,
  ITellAFriendDefault,
  SanityQuery,
  getTellAFriendCompetitionData,
  getTellAFriendData,
} from "@chef/feature-sanity";

import { Share } from "../../components/TellAFriend/Share";
import { useCompetitionDateValidationData } from "../../hooks/useCompetitionDateValidationData";
import { TellAFriendLayout } from "../../components/TellAFriend/TellAFriendLayout";
import { useScrollToCompetitionInformation } from "../../hooks/useScrollToCompetitionInformation";

import { intl } from "./TellAFriendPage.Intl";

interface TellAFriendPageProps {
  tellAFriendDefault: ITellAFriendDefault;
  tellAFriendCompetition: ITellAFriendCompetition;
}

export const TellAFriendPage = ({
  tellAFriendDefault,
  tellAFriendCompetition,
}: TellAFriendPageProps) => {
  const {
    heading,
    smsMessage,
    invitationBlock: { howItWorks, title },
  } = tellAFriendDefault;

  const { competitionSetting } = tellAFriendCompetition;
  const dateSetting = competitionSetting?.dateSetting;

  const { deliveryCutoffDate, endDate, startDate } = dateSetting;

  const { isCompetitionPeriod } = useCompetitionDateValidationData({
    startDate,
    endDate,
    deliveryCutoffDate,
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  const { openCompetitionInformation } = useScrollToCompetitionInformation({
    scrollRef,
  });

  return (
    <AppShell title={intl.REFER_A_FRIEND}>
      <AppShell.Meta />
      <AppShell.Header />
      <AppShell.AccountPausedBanner />
      <AppShell.OldVippsBanner />
      <AppShell.Main>
        <BlockContent body={heading} />

        {isCompetitionPeriod && (
          <button
            onClick={openCompetitionInformation}
            className="flex justify-center pb-6 mx-auto underline text-primary"
          >
            <strong>{intl.READ_MORE_ABOUT_THE_CAMPAIGN}</strong>
          </button>
        )}

        <TellAFriendLayout
          tellAFriendCompetition={tellAFriendCompetition}
          competitionInformationRef={scrollRef}
        >
          <Share
            defaultBlockHeading={
              <BlockContent body={title} prose={{ disabled: true }} />
            }
            smsMessage={smsMessage}
            howItWorks={
              <BlockContent body={howItWorks} prose={{ padding: "none" }} />
            }
          />
        </TellAFriendLayout>
      </AppShell.Main>

      <AppShell.Footer minified />
      <AppShell.TabBar />
    </AppShell>
  );
};

export const TellAFriendGetStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    tellAFriendDefault: ITellAFriendDefault;
    tellAFriendCompetition: ITellAFriendCompetition;
  },
  SanityQuery
> = async ({ draftMode = false }) => {
  const tellAFriendDefault = await getTellAFriendData(null, {
    preview: draftMode,
  });
  const tellAFriendCompetition = await getTellAFriendCompetitionData(null, {
    preview: draftMode,
  });

  if (!tellAFriendDefault || !tellAFriendCompetition) {
    return {
      notFound: true,
      revalidate: 600,
    };
  }

  return {
    props: {
      draftMode,
      tellAFriendDefault,
      tellAFriendCompetition,
    },
    revalidate: 600,
  };
};
